import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { bindActionCreators } from 'redux';

import type { CouponOfferType } from '@exp/components/products/constants';

import {
  addFavorite,
  claimCoupon,
  getCampaignsForStoreIds,
  getCouponsForStore,
  getCouponsForUser,
  getPendingProductNotifications,
  getProductDetails,
  getProductFavorite,
  getProductModules,
  getProductSuggestions,
  getRatingSummaryForProduct,
  removeFavorite,
} from '../js/common/api';

import { fetchCartSuggestions, fetchCartSuggestionsByProduct } from '../cart/cartActions';
import { fetchGuidelines } from '../store/components/guidelines/guidelinesReducer';
import type ProductTypes from './productTypes';

export const addProductFavorite = createAsyncThunk('product/favorite/add',
  (favoriteArgs: { organizationId: number, productCode: string, sku: string, storeId: number }) =>
    addFavorite(favoriteArgs));

export const claimProductCoupon = createAsyncThunk('product/coupons/claim',
  ({ couponId }: { couponId: number }) =>
    claimCoupon({ couponId }));

export const fetchProductCoupons = createAsyncThunk('product/coupons/fetch',
  ({ orgStoreIds }: { orgStoreIds: Record<number, number[]> }) =>
    getCouponsForUser({ orgStoreIds }));

export const fetchProductFavorite = createAsyncThunk('product/favorite/fetch',
  ({ organizationId, productCode }: { organizationId: number, productCode: string }) =>
    getProductFavorite(organizationId, productCode));

export const fetchProductInfo = createAsyncThunk('product/info/fetch',
  ({ organizationId, productCode }: { organizationId: number, productCode: string }) =>
    getProductDetails(organizationId, productCode));

export const fetchProductModules = createAsyncThunk('product/modules/fetch',
  ({ organizationId, productCode }: { organizationId: number, productCode: string }) =>
    getProductModules(productCode, organizationId));

export const fetchProductNotifications = createAsyncThunk('product/notifications/fetch',
  ({ catalogProductId, organizationId, storeId }:
    { catalogProductId: number, organizationId: number, storeId: number }) =>
    getPendingProductNotifications(organizationId, storeId, catalogProductId));

export const fetchProductSuggestions = createAsyncThunk('product/suggestions/fetch',
  (options: { orgId: number, productCode: string, productLimit?: number, version?: number }) =>
    getProductSuggestions(options));

export const fetchRatingSummary = createAsyncThunk('product/ratingSummary/fetch',
  ({ organizationId, productCode }: { organizationId: number, productCode: string }) =>
    getRatingSummaryForProduct(organizationId, productCode));

export const fetchStoreCampaigns = createAsyncThunk('store/campaigns/fetch',
  (storeIds: number[]) =>
    getCampaignsForStoreIds(storeIds));

export const fetchStoreCoupons = createAsyncThunk('store/coupons/fetch',
  ({ orgStoreIds, types }: { orgStoreIds: Record<number | string, number[]>, types: CouponOfferType[] }) =>
    getCouponsForStore({ orgStoreIds, types }));

export const removeProductFavorite = createAsyncThunk('product/favorite/remove',
  (id: number) =>
    removeFavorite(id));

export const selectProductOption = createAction<Record<string, string>>('product/option/select');

export const setRatingSummary = createAction<ProductTypes.RatingSummary>('product/ratingSummary/set');

export const setSelectedDeal = createAction<ProductTypes.Store>('product/deal/set');

export const updateQuantity = createAction<number>('product/quantity/set');

const productDetailsActionsDispatch = (dispatch) => bindActionCreators({
  addProductFavorite,
  claimProductCoupon,
  fetchCartSuggestions,
  fetchCartSuggestionsByProduct,
  fetchGuidelines,
  fetchProductCoupons,
  fetchProductFavorite,
  fetchProductInfo,
  fetchProductModules,
  fetchProductNotifications,
  fetchProductSuggestions,
  fetchRatingSummary,
  fetchStoreCampaigns,
  fetchStoreCoupons,
  removeProductFavorite,
  selectProductOption,
  setRatingSummary,
  setSelectedDeal,
  updateQuantity,
}, dispatch);

export default productDetailsActionsDispatch;
